import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Container, Box, Avatar, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, MailOutline } from '@mui/icons-material';
import { api } from '../../services/api';

const ConfirmEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email, please wait...');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!userId || !token) {
        setStatus('error');
        setMessage('Missing or invalid email confirmation parameters.');
        return;
      }

      try {
        const response = await api.confirmEmail(userId, token);
        if (response.data?.success) {
          setStatus('success');
          setMessage(response.data.message || 'Your email has been confirmed successfully!');
        } else {
          setStatus('error');
          setMessage(response.data?.message || 'Email verification failed.');
        }
      } catch (err: any) {
        console.error('Email verification failed:', err);
        let errorMsg = 'An error occurred during email verification.';
        if (err.response?.data?.message) {
          errorMsg = err.response.data.message;
        } else if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          errorMsg = err.response.data.errors.join(' ');
        }
        setStatus('error');
        setMessage(errorMsg);
      }
    };

    verifyEmail();
  }, [userId, token]);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          {status === 'loading' && (
            <>
              <Avatar sx={{ m: 1, bgcolor: 'primary.main', width: 56, height: 56 }}>
                <MailOutline sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography component="h1" variant="h5" sx={{ mt: 2, mb: 3, fontWeight: 'bold' }}>
                Email Verification
              </Typography>
              <CircularProgress sx={{ my: 2 }} />
              <Typography variant="body1" align="center" color="text.secondary" sx={{ mt: 2 }}>
                {message}
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Avatar sx={{ m: 1, bgcolor: 'success.main', width: 56, height: 56 }}>
                <CheckCircleOutline sx={{ fontSize: 36 }} />
              </Avatar>
              <Typography component="h1" variant="h5" sx={{ mt: 2, mb: 3, fontWeight: 'bold', color: 'success.main' }}>
                Success!
              </Typography>
              <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                {message}
              </Alert>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 2 }}
              >
                Sign In
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Avatar sx={{ m: 1, bgcolor: 'error.main', width: 56, height: 56 }}>
                <ErrorOutline sx={{ fontSize: 36 }} />
              </Avatar>
              <Typography component="h1" variant="h5" sx={{ mt: 2, mb: 3, fontWeight: 'bold', color: 'error.main' }}>
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                {message}
              </Alert>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                variant="outlined"
                color="primary"
                size="large"
                sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 2 }}
              >
                Go to Sign In
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ConfirmEmailPage;
